import React, { useMemo, useEffect } from "react";
import { Drawer, Box, Typography, Divider } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
} from "@store/slices/chart-config-slice";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { useGetDatasetByIdQuery, useGetWeightConfigurationsQuery } from "@store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type PointInTimeUnit,
  type GuardrailRollingOption,
  type ViewerSelectableTimePeriod,
  type PointInTimeViewerOptions,
} from "@models/Dataset";
import { updateViewerOptions } from "@store/slices/chart-config-slice";
import PointInTimeViewerOptionsSection from "../PointInTimeViewerOptionsSection";
import { useBarChartData } from "./hooks/useBarChartData";
import { useBarChartHandlers } from "./hooks/useBarChartHandlers";
import { useBarChartDerivedState } from "./hooks/useBarChartDerivedState";
import DrawerHeader from "./components/DrawerHeader";
import OrientationSection from "./components/OrientationSection";
import FormattingSection from "./components/FormattingSection";
import DisplayNSection from "./components/DisplayNSection";
import DataSortingSection from "./components/DataSortingSection";
import BarCustomizationSection from "./components/BarCustomizationSection";
import FocusModeSection from "./components/FocusModeSection";
import TimePeriodSection from "./components/TimePeriodSection";
import StatisticalTestingSection from "./components/StatisticalTestingSection";
import StylingSection from "./components/StylingSection";
import PreviewPanel from "./components/PreviewPanel";
import DrawerFooter from "./components/DrawerFooter";

const BarChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetName, activeFilters, datasetId } =
    useAppSelector(chartConfigSelector);
  const { isDrawerOpen, editingWidgetId } = useAppSelector(chartDrawerSelector);

  const { data: datasetDetails } = useGetDatasetByIdQuery(datasetId as string, {
    skip: !datasetId,
  });

  // ─── Point-in-Time Date Guardrails ────────────────────────────────────────
  const pitGuardrails = useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);

  const currentPitUnit = useMemo((): PointInTimeUnit | null => {
    const tp = currentConfig.timePeriodSelection?.timePeriod;
    if (tp === "latest_quarter" || tp === "previous_quarter") return "quarter";
    if (tp === "latest_semester" || tp === "previous_semester")
      return "semester";
    return null;
  }, [currentConfig.timePeriodSelection?.timePeriod]);

  const pitUnitGuardrail = currentPitUnit
    ? pitGuardrails.units[currentPitUnit]
    : null;

  const pitGuardrailAllowedWindowSizes: number[] | undefined =
    pitUnitGuardrail?.allowedRollingAverages.filter((n) => n !== 0);

  const pitGuardrailNoRollingDisabled: boolean = pitUnitGuardrail
    ? !pitUnitGuardrail.allowedRollingAverages.includes(
        0 as GuardrailRollingOption,
      )
    : false;

  // When the dataset only allows "No Rolling Average" for the current PIT unit,
  // the "Apply Rolling Average" checkbox should be disabled & forced off.
  const pitGuardrailRollingDisabled: boolean =
    !!pitUnitGuardrail && (pitGuardrailAllowedWindowSizes?.length ?? 0) === 0;

  const pitGuardrailConflict = useMemo((): string | null => {
    if (!pitUnitGuardrail || !currentPitUnit) return null;
    const isRollingEnabled = currentConfig.rollingAverage?.enabled ?? false;
    const windowSize = (currentConfig.rollingAverage?.windowSize ??
      0) as GuardrailRollingOption;
    const effectiveOption: GuardrailRollingOption = isRollingEnabled
      ? windowSize
      : 0;
    if (!pitUnitGuardrail.allowedRollingAverages.includes(effectiveOption)) {
      return isRollingEnabled
        ? `Rolling average of ${windowSize} periods is no longer allowed for ${currentPitUnit} by dataset policy.`
        : `No Rolling Average is no longer allowed for ${currentPitUnit} by dataset policy.`;
    }
    return null;
  }, [pitUnitGuardrail, currentPitUnit, currentConfig.rollingAverage]);

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateOrientation,
    updatePercentageFormatting,
    updateValuePlacement,
    updateDataSorting,
    updateTopNResponses,
    updateCombineRemainingIntoOther,
    updateColorPalette,
    updateLabelsPlacement,
    updateShowLegend,
    updateTruncateCategoryText,
    updateShowValueAxis,
    updateCompactMode,
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updateFocusSelection,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateWeight,
    updateBarHidden,
    updateBarPin,
    updateBarLabelOverride,
    updateBarManualColor,
    updateBarCustomOrder,
    updateShowBarN,
    updateShowChartN,
  } = chartConfigDispatcher(dispatch);

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  // When creating a new chart, default the weight to DATASET_DEFAULT if the
  // selected dataset has at least one weight configuration.
  useEffect(() => {
    if (
      editingWidgetId === "new-widget" &&
      (weightConfigData?.weightConfigurations?.length ?? 0) > 0 &&
      currentConfig.weight === "DISABLED"
    ) {
      updateWeight("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWidgetId, weightConfigData]);

  const {
    fetchChartData,
    isRefetchingData,
    weightsApplied,
    setWeightsApplied,
  } = useBarChartData();

  const {
    localApplyFilters,
    isTogglingFilters,
    usePreviewDateFilter,
    handleFilterToggle,
    handlePreviewDateFilterToggle,
    handleWeightChange,
    handleTimePeriodChange,
    handleTimePeriodDatesChange,
    handleStatTestingChange,
    handleRollingAverageEnabledChange,
    handleRollingAverageWindowSizeChange,
    handleSave,
    handleDiscard,
    isCreatingChart,
    updateFocusParams,
  } = useBarChartHandlers({
    fetchChartData,
    isRefetchingData,
    setWeightsApplied,
    pitGuardrails,
  });

  const {
    questions,
    allVariables,
    allValues,
    isAvailableData,
    canSave,
    hasSampleSizeBelowThreshold,
    hasPartialHistoricalData,
    hasNoPreviousPeriodData,
    headerTitle,
    isAllTimePeriod,
  } = useBarChartDerivedState(isRefetchingData, datasetDetails?.dataAvailabilityDate);

  const isDisabled = isRefetchingData || isTogglingFilters;

  const isViewerOptionsInvalid =
    currentPitUnit !== null &&
    currentConfig.viewerOptions.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalPeriods).length === 0 &&
    currentConfig.viewerOptions.chartPeriodAdditionalRollingAverages.length ===
      0;

  const dashboardDateOverride = useMemo(() => {
    if (!usePreviewDateFilter) return undefined;
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    if (dfStart && dfEnd) {
      return { startDate: dfStart, endDate: dfEnd };
    }
    return undefined;
  }, [usePreviewDateFilter]);

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{
        "& .MuiDrawer-paper": {
          width: "80vw",
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <DrawerHeader
          questionLabel={questions?.label}
          headerTitle={headerTitle}
          datasetName={datasetName}
          activeFiltersCount={activeFilters.length}
          localApplyFilters={localApplyFilters}
          isTogglingFilters={isTogglingFilters}
          isRefetchingData={isRefetchingData}
          onFilterToggle={handleFilterToggle}
          usePreviewDateFilter={usePreviewDateFilter}
          onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
        />

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Configuration Panel */}
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              Configurations
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <OrientationSection
                orientation={currentConfig.orientation}
                onOrientationChange={updateOrientation}
              />

              <FormattingSection
                percentageFormatting={currentConfig.percentageFormatting}
                valuePlacement={currentConfig.valuePlacement}
                weight={currentConfig.weight}
                weightsApplied={weightsApplied}
                isRefetchingData={isRefetchingData}
                onPercentageFormattingChange={updatePercentageFormatting}
                onValuePlacementChange={updateValuePlacement}
                onWeightChange={handleWeightChange}
              />

              <DataSortingSection
                dataSorting={currentConfig.dataSorting}
                topNResponses={currentConfig.topNResponses}
                topNValue={currentConfig.topNValue}
                combineRemainingIntoOther={
                  currentConfig.combineRemainingIntoOther
                }
                onDataSortingChange={updateDataSorting}
                onTopNResponsesChange={updateTopNResponses}
                onCombineRemainingChange={updateCombineRemainingIntoOther}
              />

              <Divider />

              {questions?.type === "Single" && currentConfig.focusSelection && (
                <FocusModeSection
                  focusSelection={currentConfig.focusSelection}
                  allVariables={allVariables}
                  allValues={allValues}
                  isDisabled={isDisabled}
                  onFocusSelectionChange={updateFocusSelection}
                  onFocusParamsChange={updateFocusParams}
                />
              )}

              <TimePeriodSection
                timePeriodSelection={currentConfig.timePeriodSelection}
                rollingAverage={currentConfig.rollingAverage}
                statisticalTestingEnabled={
                  currentConfig.statisticalTesting?.enabled || false
                }
                responsesMinDate={currentConfig.responsesMinDate}
                responsesMaxDate={currentConfig.responsesMaxDate}
                isDisabled={isDisabled}
                totalAvailablePeriods={currentConfig.categories?.length}
                onTimePeriodChange={handleTimePeriodChange}
                onTimePeriodDatesChange={handleTimePeriodDatesChange}
                onRollingAverageEnabledChange={
                  handleRollingAverageEnabledChange
                }
                onRollingAverageWindowSizeChange={
                  handleRollingAverageWindowSizeChange
                }
                guardrailAllowedWindowSizes={pitGuardrailAllowedWindowSizes}
                guardrailNoRollingDisabled={pitGuardrailNoRollingDisabled}
                guardrailRollingDisabled={pitGuardrailRollingDisabled}
                guardrailConflictWarning={pitGuardrailConflict ?? undefined}
              />

              {currentPitUnit && (
                <>
                  <Divider />
                  <PointInTimeViewerOptionsSection
                    viewerOptions={currentConfig.viewerOptions}
                    chartTimePeriod={
                      currentConfig.timePeriodSelection
                        ?.timePeriod as ViewerSelectableTimePeriod
                    }
                    chartRollingWindowSize={
                      currentConfig.rollingAverage?.enabled
                        ? (currentConfig.rollingAverage?.windowSize ?? 0)
                        : 0
                    }
                    pitGuardrailUnits={pitGuardrails.units}
                    isDisabled={isDisabled}
                    onViewerOptionsChange={(opts: PointInTimeViewerOptions) =>
                      dispatch(updateViewerOptions(opts))
                    }
                  />
                </>
              )}

              <BarCustomizationSection
                bars={currentConfig.series.map((s) => ({
                  name: s.name,
                  color: s.color || "#57bfe7",
                }))}
                barCustomization={currentConfig.barCustomization}
                dataSorting={currentConfig.dataSorting}
                colorPalette={currentConfig.colorPalette}
                onToggleVisibility={(barName) => {
                  const isHidden =
                    currentConfig.barCustomization.hiddenBars.includes(barName);
                  updateBarHidden({ barName, hidden: !isHidden });
                }}
                onToggleAll={(visible) => {
                  if (visible) {
                    // Show all: clear hiddenBars
                    currentConfig.barCustomization.hiddenBars.forEach(
                      (barName) =>
                        updateBarHidden({ barName, hidden: false }),
                    );
                  } else {
                    // Hide all except one: hide all but first visible
                    const visibleBars = currentConfig.series.filter(
                      (s) =>
                        !currentConfig.barCustomization.hiddenBars.includes(
                          s.name,
                        ),
                    );
                    visibleBars
                      .slice(1)
                      .forEach((s) =>
                        updateBarHidden({ barName: s.name, hidden: true }),
                      );
                  }
                }}
                onPinChange={(barName, position) =>
                  updateBarPin({ barName, position })
                }
                onLabelChange={(barName, label) =>
                  updateBarLabelOverride({ barName, label })
                }
                onResetLabel={(barName) =>
                  updateBarLabelOverride({ barName, label: barName })
                }
                onCustomOrderChange={(newOrder) =>
                  updateBarCustomOrder(newOrder)
                }
              />

              <DisplayNSection
                showBarN={currentConfig.showBarN}
                showChartN={currentConfig.showChartN}
                onShowBarNChange={updateShowBarN}
                onShowChartNChange={updateShowChartN}
              />

              <Divider />

              <StatisticalTestingSection
                statisticalTesting={currentConfig.statisticalTesting}
                isDisabled={isDisabled}
                isAllTimePeriod={isAllTimePeriod}
                rollingAverageEnabled={
                  currentConfig.rollingAverage?.enabled || false
                }
                onStatTestingChange={handleStatTestingChange}
                updateStatTestingEnabled={updateStatTestingEnabled}
                updateStatTestType={updateStatTestType}
                updateSignificanceLevel={updateSignificanceLevel}
                updateMinimumSampleSize={updateMinimumSampleSize}
              />

              <Divider />

              <StylingSection
                title={currentConfig.title}
                subtitle={currentConfig.subtitle}
                titleBold={currentConfig.titleBold}
                titleUnderline={currentConfig.titleUnderline}
                colorPalette={currentConfig.colorPalette}
                labelsPlacement={currentConfig.labelsPlacement}
                showLegend={currentConfig.showLegend}
                truncateCategoryText={currentConfig.truncateCategoryText}
                showValueAxis={currentConfig.showValueAxis}
                compactMode={currentConfig.compactMode}
                barColorEntries={currentConfig.series
                  .filter(
                    (s) =>
                      !currentConfig.barCustomization.hiddenBars.includes(
                        s.name,
                      ),
                  )
                  .map((s) => ({
                    name: s.name,
                    displayName:
                      currentConfig.barCustomization.labelOverrides[s.name] ??
                      s.name,
                    fallbackColor: s.color || "#57bfe7",
                  }))}
                manualColors={currentConfig.barCustomization.manualColors}
                onManualColorChange={(barName, color) =>
                  updateBarManualColor({ barName, color })
                }
                onTitleChange={updateChartTitle}
                onSubtitleChange={updateChartSubtitle}
                onTitleBoldChange={updateTitleBold}
                onTitleUnderlineChange={updateTitleUnderline}
                onColorPaletteChange={updateColorPalette}
                onLabelsPlacementChange={updateLabelsPlacement}
                onShowLegendChange={updateShowLegend}
                onTruncateCategoryTextChange={updateTruncateCategoryText}
                onShowValueAxisChange={updateShowValueAxis}
                onCompactModeChange={updateCompactMode}
              />
            </Box>
          </Box>

          {/* Preview Panel */}
          <PreviewPanel
            currentConfig={currentConfig}
            questions={questions}
            isRefetchingData={isRefetchingData}
            isAvailableData={isAvailableData}
            hasSampleSizeBelowThreshold={hasSampleSizeBelowThreshold}
            hasPartialHistoricalData={hasPartialHistoricalData}
            hasNoPreviousPeriodData={hasNoPreviousPeriodData}
            dashboardDateOverride={dashboardDateOverride}
            dataAvailabilityDate={datasetDetails?.dataAvailabilityDate}
          />
        </Box>

        <DrawerFooter
          isCreatingChart={isCreatingChart}
          canSave={canSave && !pitGuardrailConflict && !isViewerOptionsInvalid}
          isRefetchingData={isRefetchingData}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </Box>
    </Drawer>
  );
};

export default BarChartConfigDrawer;
